/**
 * TaxonomyContext — loads taxonomy domains once at login and makes them
 * available globally. Avoids repeated fetches across pages.
 */
import { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { articlesApi, TaxonomyDomain } from '../../api/articlesApi';

interface TaxonomyContextValue {
  domains: TaxonomyDomain[];
  isLoading: boolean;
  /** Resolve a domain ID to its display name, falling back to the ID itself */
  getDomainName: (domainId: string) => string;
}

const TaxonomyContext = createContext<TaxonomyContextValue>({
  domains: [],
  isLoading: false,
  getDomainName: (id) => id,
});

export function TaxonomyProvider({ children }: { children: ReactNode }) {
  const { data: domains = [], isLoading } = useQuery({
    queryKey: ['taxonomy', 'domains'],
    queryFn: () => articlesApi.getTaxonomyDomains(),
    staleTime: 10 * 60 * 1000, // 10 minutes — domains change infrequently
    gcTime: 30 * 60 * 1000,
  });

  const getDomainName = (domainId: string): string => {
    const match = domains.find((d) => d.domainId === domainId);
    return match?.domainName ?? domainId;
  };

  return (
    <TaxonomyContext.Provider value={{ domains, isLoading, getDomainName }}>
      {children}
    </TaxonomyContext.Provider>
  );
}

export function useTaxonomy() {
  return useContext(TaxonomyContext);
}
